import { Component, createRef, type ErrorInfo, type ReactNode } from "react";
import styles from "./AppErrorBoundary.module.css";

interface Props {
  children: ReactNode;
  onReload?: () => void;
}

interface State {
  message: string | null;
  errorStack: string;
  componentStack: string;
}

function reloadPage() {
  window.location.reload();
}

/** Last-resort recovery for failures in the app shell, providers, or their portals. */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { message: null, errorStack: "", componentStack: "" };
  private heading = createRef<HTMLHeadingElement>();

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : typeof error === "string"
          ? error
          : "An unexpected rendering error occurred.";
    return { message, errorStack: error instanceof Error ? (error.stack ?? "") : "", componentStack: "" };
  }

  override componentDidCatch(_error: unknown, info: ErrorInfo) {
    // Keep diagnostics in this boundary's memory only. No network, browser storage, or app state is read.
    this.setState({ componentStack: info.componentStack ?? "" });
    this.heading.current?.focus({ preventScroll: true });
    this.heading.current?.scrollIntoView({ block: "start" });
  }

  override render() {
    if (this.state.message === null) return this.props.children;
    return (
      <main className={styles.page}>
        <section className={styles.panel} aria-labelledby="app-error-title">
          <h1 id="app-error-title" ref={this.heading} tabIndex={-1} className={styles.title}>
            The app hit an error
          </h1>
          <p>Reload the page to continue.</p>
          <p className={styles.help}>If you were saving something, check whether it was saved before trying again.</p>
          <button type="button" className={styles.reload} onClick={this.props.onReload ?? reloadPage}>
            Reload page
          </button>
          <details className={styles.details}>
            <summary>Technical details</summary>
            <pre className={styles.diagnostic}>
              {this.state.message}
              {this.state.errorStack ? `\n\nJavaScript stack:\n${this.state.errorStack}` : ""}
              {this.state.componentStack ? `\n\nComponent stack:${this.state.componentStack}` : ""}
            </pre>
          </details>
        </section>
      </main>
    );
  }
}
